import { useState, useRef, useEffect, Fragment, type FC } from 'react';
import classnames from 'classnames';
import { type JumpSection } from './DesignDirectoryTypes.ts';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { createClickTrackingEvent } from '../helpers/trackingHelpers.ts';
import { type DesignsDirectory_viewer$data as Viewer } from './__generated__/DesignsDirectory_viewer.graphql.ts';
import { type DesignsDirectory_iconicDesignsDirectoryPage$data as IconicDesignsDirectoryPage } from './__generated__/DesignsDirectory_iconicDesignsDirectoryPage.graphql.ts';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { DesignsDirectoryQuickJumpLazy } from './DesignsDirectoryQuickJump/DesignsDirectoryQuickJumpLazy.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';

// styles
import styles from './DesignsDirectory.scss';

const trackClick = createClickTrackingEvent('directory list iconic design clicked');

const DesignsDirectoryComponent: FC<Props> = ({ viewer, isMobile, iconicDesignsDirectoryPage }) => {
    const { isDesignsDirectoryQuickJumpDisabled } = viewer;
    const { directory } = iconicDesignsDirectoryPage;
    const [jumpSections, setJumpSections] = useState<Array<JumpSection>>([]);
    const sections = useRef<Array<JumpSection>>([]);
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        if (sections.current.length) {
            setJumpSections(sections.current);
        }
    }, []);

    return (
        <HeadingLevel>
            {Heading => (
                <Fragment>
                    <Heading id="all-designs" className={styles.title}>
                        <div data-tn="all-designs-title">
                            <FormattedMessage
                                id="designs.designsDirectory.title"
                                defaultMessage="All Designs"
                            />
                        </div>
                    </Heading>
                    <div className={styles.container} data-tn="all-designs-container">
                        {!isDesignsDirectoryQuickJumpDisabled && isMobile && isClient && (
                            <DesignsDirectoryQuickJumpLazy jumpSections={jumpSections} />
                        )}
                        {directory.map((dir, index) => {
                            const { label, designs } = dir || {};
                            return (
                                <Fragment key={`${index}-${label}`}>
                                    <div
                                        className={styles.label}
                                        ref={element => {
                                            if (label && element) {
                                                sections.current[index] = { label, element };
                                            }
                                        }}
                                    >
                                        {label}
                                    </div>
                                    <div
                                        data-tn={`alphabet-container-${label}`}
                                        className={classnames({
                                            [styles.designs]: !isMobile,
                                            [styles.mobileDesigns]: isMobile,
                                        })}
                                    >
                                        {designs?.map(design => {
                                            const { name, urlLabel, creators } = design || {};
                                            return (
                                                <HeadingLevel key={`${name}=${urlLabel}`}>
                                                    {() => (
                                                        <div
                                                            className={classnames({
                                                                [styles.design]: !isMobile,
                                                                [styles.mobileDesign]: isMobile,
                                                            })}
                                                        >
                                                            <Link
                                                                onClick={trackClick}
                                                                className={styles.link}
                                                                href={urlLabel || undefined}
                                                            >
                                                                <Heading
                                                                    className={styles.name}
                                                                    dataTn="designer-name"
                                                                >
                                                                    {name}
                                                                </Heading>

                                                                <Heading
                                                                    className={styles.creators}
                                                                >
                                                                    {creators}
                                                                </Heading>
                                                            </Link>
                                                        </div>
                                                    )}
                                                </HeadingLevel>
                                            );
                                        })}
                                    </div>
                                    <div className={styles.spacer} />
                                </Fragment>
                            );
                        })}
                    </div>
                </Fragment>
            )}
        </HeadingLevel>
    );
};

type Props = {
    viewer: Viewer;
    isMobile: boolean;
    iconicDesignsDirectoryPage: IconicDesignsDirectoryPage;
};

export const DesignsDirectory = createFragmentContainer(DesignsDirectoryComponent, {
    viewer: graphql`
        fragment DesignsDirectory_viewer on Viewer {
            isDesignsDirectoryQuickJumpDisabled: isEnabled(
                key: "isDesignsDirectoryQuickJumpDisabled"
            )
        }
    `,
    iconicDesignsDirectoryPage: graphql`
        fragment DesignsDirectory_iconicDesignsDirectoryPage on IconicDesignsDirectoryPage {
            directory {
                label
                designs {
                    name
                    creators
                    urlLabel
                }
            }
        }
    `,
});
